import { motion } from "framer-motion";
import { Package, Network, Terminal, Circuitry, Sparkle, Camera, CloudArrowUp, Browser, WindowsLogo } from "@phosphor-icons/react";
import { useLanguage } from "../i18n";

const icons = [CloudArrowUp, Package, Network, Terminal, WindowsLogo, Circuitry, Sparkle, Camera, Browser];

export const Services = () => {
  const { t } = useLanguage();
  return (
    <section id="services" data-testid="services-section" className="relative py-28 md:py-40 overflow-hidden">
      <span className="absolute -top-8 right-4 md:right-10 font-display font-bold text-[26vw] md:text-[18rem] leading-none text-white/[0.03] select-none pointer-events-none" aria-hidden="true">
        02
      </span>
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="font-mono text-xs uppercase tracking-[0.3em] text-brand-cyan mb-6" data-testid="services-chapter-label">
          {t.chapters.services}
        </motion.p>
        <div className="max-w-2xl mb-16 md:mb-24">
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-semibold tracking-tight text-4xl md:text-5xl leading-[1.05]"
            data-testid="services-title"
          >
            {t.services.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mt-5 font-mono text-sm text-white/50 leading-relaxed"
            data-testid="services-subtitle"
          >
            {t.services.sub}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-[minmax(190px,auto)]" data-testid="services-grid">
          {t.services.items.map((s, i) => {
            const Icon = icons[i % icons.length];
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -6 }}
                data-testid={`service-card-${i}`}
                className={`group relative rounded-lg border border-white/5 bg-ink-surface p-7 md:p-8 flex flex-col justify-between overflow-hidden transition-[border-color,box-shadow] duration-500 hover:border-brand-cyan/30 hover:shadow-[0_0_40px_-10px_rgba(0,240,255,0.25)] ${
                  s.wide ? "md:col-span-2" : ""
                } ${s.tall ? "md:row-span-2" : ""}`}
              >
                <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-brand-cyan/0 blur-3xl transition-[background-color] duration-700 group-hover:bg-brand-cyan/10 pointer-events-none" />
                <div className="flex items-start justify-between">
                  <Icon size={34} weight="duotone" className="text-brand-cyan transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/25">{s.tag}</span>
                </div>
                <div className="mt-8">
                  <h3 className="font-display font-medium text-xl md:text-2xl tracking-tight group-hover:text-brand-cyan transition-colors duration-300">{s.title}</h3>
                  <p className="mt-3 font-mono text-xs md:text-[13px] text-white/45 leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
